import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import ColumnDialog from "../ColumnDialog";
import SourceName from "../../../SourceName";
import ThreeDotsMenu from "components/ThreeDotsMenu";
import TextTruncate from "components/TextTruncate";
import DefaultThreeDotsMenuItem from "components/ThreeDotsMenu/Components/DefaultThreeDotsMenuItem";
import { processVariablesSelector } from "store/global/global.selectors";
import { workspaceInsightsSelector, workspaceRecipesSelector, recipeCustomVariablesSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { tableColumnCompletenessMessageSelector } from "store/rs-workspace/selectors/completeness.selectors";
import { getColumnLabels } from "../../utils";
import { prepareItemsOnMove } from "utils/data";
import EditIcon from "icons/settings.svg";
import DeleteIcon from "icons/delete.svg";
import ArrowUpIcon from "icons/icn-arrow-up.svg";
import BackIcon from "icons/back.svg";
import * as styles from "./styles.module.scss";
const ColumnName = ({ column, columns, rows, threeDotsPopoverRef, editable, deleteColumn, moveColumns, upsert, }) => {
    const [columnDialogOpen, setColumnDialogOpen] = useState(false);
    const [threeDotsMenuOpen, setThreeDotsMenuOpen] = useState(false);
    const processVariables = useSelector(processVariablesSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const workspaceRecipes = useSelector(workspaceRecipesSelector);
    const completenessMessage = useSelector(tableColumnCompletenessMessageSelector(column, rows));
    const recipeIds = useMemo(() => workspaceRecipes.map(({ id }) => id), [workspaceRecipes]);
    const recipeCustomVariables = useSelector(recipeCustomVariablesSelector(recipeIds));
    const { unitsLabel, typeLabel, parameterLabel } = useMemo(() => getColumnLabels(column.parameter, processVariables, insights, recipeCustomVariables, workspaceRecipes), [column.parameter, insights, processVariables, recipeCustomVariables, workspaceRecipes]);
    const moveColumn = useCallback((direction, toEnd) => {
        moveColumns(prepareItemsOnMove(columns, column, direction, toEnd));
    }, [column, columns, moveColumns]);
    const menuItems = useMemo(() => {
        return [
            {
                text: "Edit",
                icon: React.createElement(EditIcon, null),
                onClick: () => setColumnDialogOpen(true),
            },
            {
                text: "Delete",
                icon: React.createElement(DeleteIcon, null),
                onClick: () => column?.id && deleteColumn(column?.id),
            },
            {
                text: "Move to the left",
                icon: React.createElement(BackIcon, { className: styles["moveToIcon-left"] }),
                onClick: () => moveColumn("before", true),
                disabled: column.position === 1,
            },
            {
                text: "Move left",
                icon: React.createElement(ArrowUpIcon, { className: styles["moveIcon-left"] }),
                onClick: () => moveColumn("before"),
                disabled: column.position === 1,
            },
            {
                text: "Move right",
                icon: React.createElement(ArrowUpIcon, { className: styles["moveIcon-right"] }),
                onClick: () => moveColumn("after"),
                disabled: column.position === columns.length,
            },
            {
                text: "Move to the right",
                icon: React.createElement(BackIcon, { className: styles["moveToIcon-right"] }),
                onClick: () => moveColumn("after", true),
                disabled: column.position === columns.length,
            },
        ];
    }, [column?.id, column.position, columns.length, deleteColumn, moveColumn]);
    return (React.createElement("div", { className: styles.root },
        React.createElement(SourceName, { key: column.id, className: styles["columnName-container"], tooltip: completenessMessage },
            React.createElement("div", { className: styles.columnName },
                React.createElement(TextTruncate, { className: classNames(styles["columnName-title"], { [styles.incomplete]: completenessMessage }), text: `${parameterLabel} ${unitsLabel ? `(${unitsLabel})` : ""}` }),
                React.createElement(TextTruncate, { className: styles["columnName-text"], text: typeLabel }))),
        editable && (React.createElement(ThreeDotsMenu, { popoverRef: threeDotsPopoverRef, classes: {
                className: classNames(styles.threeDotsMenu, {
                    [styles["threeDotsMenu-opened"]]: threeDotsMenuOpen,
                }),
            }, items: menuItems.map((item, index) => (React.createElement(DefaultThreeDotsMenuItem, { key: index, closeMenuOnClick: () => setThreeDotsMenuOpen(false), ...item }))), closeOnTargetHide: () => setThreeDotsMenuOpen(false), isOpen: threeDotsMenuOpen, setIsOpen: setThreeDotsMenuOpen })),
        columnDialogOpen && (React.createElement(ColumnDialog, { columns: columns, rows: rows, column: column, onSubmit: upsert, onClose: () => setColumnDialogOpen(false), classes: { target: styles.columnDialogTarget } }))));
};
export default ColumnName;
